import React from 'react';

/**
 * The window of its own that a part of the page is in, see openOwnWindow, whose styles are added
 * there. Undefined in the page.
 */
export const OwnWindowContext = React.createContext<Window | undefined>(undefined);

/**
 * The empty page of a popup window, see openOwnWindow.
 */
const popupUrl = '/static/assets/dialog-window.html';

/**
 * Document Picture-in-Picture, of Chromium browsers, which TypeScript doesn't know yet.
 */
type DocumentPictureInPicture = {
  requestWindow: (options: {
    width: number;
    height: number;
    /**
     * Whether the window lacks the button that closes it and goes back to the page.
     */
    disallowReturnToOpener?: boolean;
    /**
     * Whether the window opens at the size asked for, rather than where and how large the last one
     * was.
     */
    preferInitialWindowPlacement?: boolean;
  }) => Promise<Window>;
  /**
   * The window that is open, of which a page has at most one.
   */
  window: Window | null;
};

const getPictureInPicture = () =>
  (window as Window & { documentPictureInPicture?: DocumentPictureInPicture })
    .documentPictureInPicture;

/**
 * Where a window is on the screen, and how large it is inside, see getWindowBounds.
 */
export type WindowBounds = { left: number; top: number; width: number; height: number };

export const getWindowBounds = (ownWindow: Window): WindowBounds => ({
  left: ownWindow.screenX,
  top: ownWindow.screenY,
  width: ownWindow.innerWidth,
  height: ownWindow.innerHeight,
});

/**
 * Opens a window of its own for a part of the page, e.g. dialogs or the right menu, which can be
 * moved outside the browser window, e.g. to another screen, as large as the part is. Calls ready
 * with the window once it has the styles of the page, for the part to be moved into it.
 *
 * Where the browser has it, and no other is open, it is a Picture-in-Picture window, which stays in
 * front of the other windows, and has no address bar, where the browser places it. Otherwise it is
 * a popup window, where the part is on the screen. Must be called from a click, or the browser
 * blocks the window, and failed is called instead. Where a popup window is closed before it has
 * loaded, e.g. as its page can't be loaded, closed is called instead.
 *
 * With the bounds of the last window of the part, it opens where that one was, as large as it was.
 * A Picture-in-Picture window does so as the browser remembers the last one, which Chrome does only
 * while it is asked for the same size each time: rect must then be the one of the first window.
 */
export const openOwnWindow = (
  rect: DOMRect,
  title: string,
  ready: (ownWindow: Window) => void,
  options: { last?: WindowBounds; failed?: () => void; closed?: () => void } = {},
) => {
  const { last, failed = () => undefined, closed = () => undefined } = options;
  const prepare = (ownWindow: Window) => {
    // The styles of the page, except those of styled-components, which add their own to the
    // window, see StyleSheetManager.
    document.head
      .querySelectorAll('link[rel="stylesheet"], style:not([data-styled])')
      .forEach((style) => ownWindow.document.head.append(style.cloneNode(true)));
    ownWindow.document.documentElement.dataset.theme = document.documentElement.dataset.theme;
    ownWindow.document.title = title;
    ownWindow.document.body.style.margin = '0';
    ready(ownWindow);
  };

  const pictureInPicture = getPictureInPicture();
  // Another would close the one that is open.
  if (pictureInPicture && !pictureInPicture.window) {
    pictureInPicture
      .requestWindow({
        width: rect.width,
        height: rect.height,
        // Its button back to the page closes the window, as its close button does: the part has
        // one of its own instead.
        disallowReturnToOpener: true,
        // As large as the part, where the browser places it, which can't be set. Or where the
        // browser had the last one, as large, see above.
        preferInitialWindowPlacement: !last,
      })
      .then(prepare)
      .catch(failed);
    return;
  }

  // Where the part is on the screen, below the toolbars etc. of the browser above the page.
  const { left, top, width, height } = last ?? {
    left: window.screenX + rect.left,
    top: window.screenY + window.outerHeight - window.innerHeight + rect.top,
    width: rect.width,
    height: rect.height,
  };
  const popup = window.open(
    popupUrl,
    '',
    `popup,left=${left},top=${top},width=${width},height=${height}`,
  );
  if (popup) {
    // Which no event tells before it has loaded.
    const whileLoading = setInterval(() => {
      if (popup.closed) {
        clearInterval(whileLoading);
        closed();
      }
    }, 250);
    popup.addEventListener(
      'load',
      () => {
        clearInterval(whileLoading);
        prepare(popup);
      },
      { once: true },
    );
  } else {
    failed();
  }
};
